import UploadLogoDialog from '@/shared/ui/components/Dialogs/Company/UploadLogoDialog';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';
import type { ComponentType } from 'react';

type UploadLogicProps = {
  modalTitle?: string;
  onUploadSuccess: (file: File) => void;
  onUploadFailure?: (error: unknown) => void;
};

export const useUploadDialog = ({ modalTitle, onUploadSuccess }: UploadLogicProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  return () => {
    const modalRef = showModal(UploadLogoDialog, {
      title: modalTitle || 'Upload File',
      hideDialog: () => {
        modalRef.hide();
      },
      onSave: file => {
        onUploadSuccess(file);
      },
      onError: err => {
        console.error('File retrieval failed:', err);
        modalRef.hide();
        enqueueSnackbar('Could not retrieve file.', { variant: 'error' });
      }
    });
  };
};

export const withUploadLogic = <P extends object>(Component: ComponentType<P>) => {
  return function WrappedComponent(props: P & UploadLogicProps) {
    const theme = useTheme();
    const showUploadLogoModal = useUploadDialog(props);

    return (
      <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={showUploadLogoModal}>
        <Component {...props} />
        <Box
          sx={{
            borderRadius: '50%',
            width: 20,
            height: 20,
            padding: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            right: -5,
            bottom: -5,
            backgroundColor: theme.palette.grey[400],
            color: 'white',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          <CloudUploadIcon sx={{ fontSize: '1rem' }} />
        </Box>
      </Box>
    );
  };
};
